import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { trackEvent } from "@/lib/ga";
import Seo from "@/components/Seo";
import LeadForm from "@/components/LeadForm";
import CtaBlock from "@/components/CtaBlock";
import GuaranteesGrid from "@/components/GuaranteesGrid";
import WeatherWidget from "@/components/WeatherWidget";
import GiveawayHomeStrip from "@/components/giveaway/GiveawayHomeStrip";
import FaqAccordion from "@/components/FaqAccordion";
import { SITE_URL } from "@/lib/api";
import { orgGraph } from "@/lib/schema";
import {
  Phone, ArrowRight, Snowflake, Flame, Wind, Wrench, ShieldCheck, Clock,
  BadgeDollarSign, Rocket, Star, MapPin, CheckCircle2,
} from "lucide-react";

const SERVICE_ICONS = {
  "Air Conditioning": Snowflake, "Heating": Flame, "Heat Pumps": Wind,
  "Indoor Air Quality": Wind, "Ductwork": Wrench, "HVAC": Wrench,
};

const WHY_ICONS = { "shield-check": ShieldCheck, "clock": Clock, "badge-dollar-sign": BadgeDollarSign, "rocket": Rocket, "wrench": Wrench, "star": Star };

function Reviews({ reviews }) {
  if (!reviews?.length) return null;
  return (
    <section className="py-16 md:py-24 bg-white" data-testid="home-reviews">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-sm font-bold uppercase tracking-[0.15em] text-launch">Trusted on the Space Coast</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-navy tracking-tight">What Our Neighbors Say</h2>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {reviews.slice(0, 4).map((r) => (
            <div key={r.id} className="bg-slate-50 border border-slate-200 rounded-lg p-6 flex flex-col" data-testid={`review-${r.id}`}>
              <div className="flex gap-0.5 text-launch">{Array.from({ length: r.rating || 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}</div>
              <p className="mt-3 text-sm text-slate-700 leading-relaxed flex-1">"{r.text}"</p>
              <div className="mt-4 text-sm"><span className="font-semibold text-navy">{r.name}</span><span className="text-slate-500"> — {r.location}</span></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { company } = useCompany();
  const { data: services = [] } = useQuery({ queryKey: ["home-services"], queryFn: async () => (await api.get("/services")).data });
  const { data: locations = [] } = useQuery({ queryKey: ["home-locations"], queryFn: async () => (await api.get("/locations")).data });
  const { data: reviews = [] } = useQuery({ queryKey: ["home-reviews"], queryFn: async () => (await api.get("/reviews")).data });

  const featured = ["ac-repair", "ac-installation", "heating", "indoor-air-quality", "heat-pumps", "hvac-maintenance-plan"]
    .map((slug) => services.find((s) => s.slug === slug)).filter(Boolean);

  const why = company.why_choose_us || [];

  const jsonLd = [
    ...orgGraph(company),
  ];

  const homeFaqs = [
    { q: "Do you offer emergency AC repair?", a: "Yes. We provide 24/7 emergency HVAC service across Cocoa and the Space Coast because Florida heat doesn't wait." },
    { q: "What areas do you serve?", a: `We serve ${company.primary_service_area || "the Space Coast"} including ${(locations.slice(0,4).map(l=>l.city).join(", "))} and the surrounding Brevard County area.` },
    { q: "Do you offer financing on new systems?", a: "Yes, we offer flexible financing so you can get the right high-efficiency system without draining your savings." },
    { q: "Are you licensed and insured?", a: "Absolutely. Aquila Air & Heating is fully Florida state-certified and insured for your protection and peace of mind." },
  ];

  return (
    <>
      <Seo
        title="Aquila Air & Heating | AC Repair & HVAC in Cocoa, FL & the Space Coast"
        description="Fast, honest AC repair, installation, and HVAC service in Cocoa & Brevard County. 24/7 emergency service, upfront pricing, financing available. Comfort Ready For Liftoff."
        path="/"
        jsonLd={jsonLd}
      />

      {/* Hero — dark premium, CSS-only background for fast LCP */}
      <section className="relative overflow-hidden text-white bg-[#0a0e15]" data-testid="hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#080b12] via-[#0d1c30] to-[#12355B]" aria-hidden="true" />
        <div className="absolute inset-0 dot-grid opacity-40" aria-hidden="true" />
        <div className="absolute -top-32 -right-24 h-[28rem] w-[28rem] rounded-full bg-cool/20 blur-3xl" aria-hidden="true" />
        <div className="absolute bottom-[-6rem] left-1/4 h-72 w-72 rounded-full bg-launch/10 blur-3xl" aria-hidden="true" />
        <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-launch via-cool to-transparent" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 lg:py-32">
          <div className="max-w-2xl">
            <p className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-launch"><Rocket className="h-4 w-4" /> Comfort Ready For Liftoff</p>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Space Coast HVAC You Can Count On
            </h1>
            <p className="mt-5 text-lg sm:text-xl text-slate-200 leading-relaxed">
              Fast, honest air conditioning &amp; heating service across Cocoa and Brevard County. Same-day repairs, high-efficiency installs, and 24/7 emergency help.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a href={`tel:${company.phone_href || ""}`} onClick={() => trackEvent("phone_click", { location: "hero" })} data-testid="hero-call-button" className="inline-flex items-center justify-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold py-3.5 px-8 rounded-md shadow-lg transition-colors">
                <Phone className="h-5 w-5" /> Call {company.phone || ""}
              </a>
              <Link to="/contact-us" onClick={() => trackEvent("estimate_click", { location: "hero" })} data-testid="hero-estimate-button" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold py-3.5 px-8 rounded-md transition-colors">
                Get a Free Estimate <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              {["Licensed & Insured", "24/7 Emergency", "Upfront Pricing", "Financing Available"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 text-sm text-slate-200"><CheckCircle2 className="h-4 w-4 text-cool" /> {t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <WeatherWidget />

      <GiveawayHomeStrip />

      {/* Install promo band */}
      <div className="bg-launch text-white" data-testid="install-promo-band">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-center gap-x-8 gap-y-1 text-center text-sm sm:text-base font-bold">
          <span>⚡ Next-Day Installs — Same-Day in Some Cases</span>
          <span className="hidden sm:inline text-white/50">•</span>
          <span>Never Any Overtime Charges</span>
        </div>
      </div>

      {/* Lifetime Warranty — primary differentiator */}
      <section className="relative bg-navy text-white overflow-hidden dot-grid" data-testid="home-lifetime-warranty">
        <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-launch/10 to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 relative">
          <div className="grid lg:grid-cols-[1fr_auto] gap-10 items-center">
            <div>
              <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-launch"><ShieldCheck className="h-4 w-4" /> The Launch Advantage</p>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.05] tracking-tight">
                Florida's First HVAC Company<br /><span className="text-launch">Offering a Lifetime Warranty.</span>
              </h2>
              <p className="mt-5 text-lg text-slate-200 max-w-2xl leading-relaxed">
                Ask us how qualifying new HVAC system installations can include lifetime warranty protection.
              </p>
              <p className="mt-3 text-xs text-slate-400 max-w-2xl">
                Available on qualifying systems and installations. Coverage is subject to applicable program terms, registration requirements, eligibility and other conditions.
              </p>
              <Link to="/lifetime-warranty" onClick={() => trackEvent("warranty_click", { location: "home_knockout" })} data-testid="home-warranty-cta"
                className="mt-8 inline-flex items-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold py-3.5 px-8 rounded-md shadow-lg transition-colors">
                Explore the Lifetime Warranty <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
            <div className="hidden lg:flex items-center justify-center">
              <div className="relative h-44 w-44 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center backdrop-blur">
                <ShieldCheck className="h-24 w-24 text-launch" strokeWidth={1.25} />
                <span className="absolute -bottom-3 bg-launch text-white text-[11px] font-extrabold uppercase tracking-wider px-3 py-1 rounded-full shadow">Lifetime</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* What Do You Need */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="home-what-you-need">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-sm font-bold uppercase tracking-[0.15em] text-launch">Where do we start?</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-navy tracking-tight">What Do You Need?</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Repair Fast.", d: "Your system isn't keeping up. We diagnose the problem and help restore your comfort.", to: "/services" },
              { n: "02", t: "Replace Right.", d: "Get the right system for your home, comfort needs and budget.", to: "/services" },
              { n: "03", t: "Breathe Better.", d: "Improve comfort, humidity control and indoor air quality throughout your home.", to: "/services" },
            ].map((c) => (
              <Link key={c.n} to={c.to} data-testid={`home-need-${c.n}`} className="group bg-white border border-slate-200 rounded-xl p-7 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                <span className="text-5xl font-extrabold text-launch/20 group-hover:text-launch/40 transition-colors">{c.n}</span>
                <h3 className="mt-3 text-2xl font-extrabold text-navy">{c.t}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{c.d}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-launch group-hover:gap-2 transition-all">Explore <ArrowRight className="h-4 w-4" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Our Guarantees */}
      <section className="py-16 md:py-24" data-testid="home-guarantees">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-sm font-bold uppercase tracking-[0.15em] text-launch">Our Promise</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-navy tracking-tight">Guarantees That Back Every Job</h2>
            <p className="mt-3 text-slate-600 leading-relaxed">We put our promises in writing — on your comfort, our workmanship, and your investment.</p>
          </div>
          <GuaranteesGrid className="mt-12" />
          <div className="mt-8">
            <Link to="/guarantees" data-testid="home-guarantees-cta" className="inline-flex items-center gap-2 font-semibold text-launch hover:gap-3 transition-all">
              See all our guarantees <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Built for Space Coast Heat */}
      <section className="py-16 md:py-24 bg-navy text-white dot-grid" data-testid="home-space-coast">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.15em] text-launch">Local Authority</p>
            <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">Built for<br />Space Coast Heat.</h2>
            <p className="mt-5 text-lg text-slate-200 leading-relaxed max-w-xl">
              Florida heat, high humidity and long cooling seasons push HVAC systems hard — and coastal salt air is tough on equipment. We know these conditions because we live and work here, proudly serving the Space Coast from Mims to Sebastian.
            </p>
            <Link to="/service-areas" data-testid="home-space-coast-cta" className="mt-8 inline-flex items-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold py-3.5 px-8 rounded-md shadow-lg transition-colors">
              View All Service Areas <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
          <div className="flex flex-wrap gap-2 content-start">
            {["Mims", "Titusville", "Port St. John", "Cocoa", "Rockledge", "Merritt Island", "Viera", "Suntree", "Melbourne", "West Melbourne", "Palm Bay", "Satellite Beach", "Indialantic", "Grant-Valkaria", "Sebastian"].map((c) => (
              <span key={c} className="inline-flex items-center gap-1.5 text-sm bg-white/5 border border-white/10 rounded-full px-3.5 py-1.5 text-slate-100"><CheckCircle2 className="h-3.5 w-3.5 text-launch" /> {c}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-sm font-bold uppercase tracking-[0.15em] text-launch">Full-Service HVAC</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-navy tracking-tight">Everything Your Home Needs to Stay Comfortable</h2>
            <p className="mt-3 text-base text-slate-600 leading-relaxed">From emergency AC repair to whole-home comfort upgrades, our certified technicians handle it all — all makes and models.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((s) => {
              const Icon = SERVICE_ICONS[s.category] || Wrench;
              return (
                <Link key={s.slug} to={`/${s.slug}`} data-testid={`home-service-${s.slug}`} aria-label={`Learn more about ${s.name}`} className="group bg-white border border-slate-200 rounded-lg p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
                  <span className="inline-flex items-center justify-center h-12 w-12 rounded-md bg-cool/10 text-cool"><Icon className="h-6 w-6" /></span>
                  <h3 className="mt-4 text-xl font-semibold text-navy">{s.name}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">{s.intro}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-launch group-hover:gap-2 transition-all">Learn more <ArrowRight className="h-4 w-4" /></span>
                </Link>
              );
            })}
          </div>
          <div className="mt-10">
            <Link to="/services" className="inline-flex items-center gap-2 border-2 border-navy text-navy hover:bg-navy hover:text-white font-semibold py-3 px-6 rounded-md transition-colors" data-testid="home-all-services">View All Services <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {/* Why choose us - bento */}
      <section className="py-16 md:py-24 bg-white" data-testid="home-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-bold uppercase tracking-[0.15em] text-launch">Why Aquila Air &amp; Heating</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-navy tracking-tight">Local Experts. Mission-Grade Service.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {why.map((w, i) => {
              const Icon = WHY_ICONS[w.icon] || ShieldCheck;
              return (
                <div key={i} className={`rounded-lg p-6 border ${i === 0 ? "md:col-span-2 bg-navy text-white border-navy dot-grid" : "bg-slate-50 border-slate-200"}`} data-testid={`why-${i}`}>
                  <span className={`inline-flex items-center justify-center h-11 w-11 rounded-md ${i === 0 ? "bg-white/10 text-launch" : "bg-cool/10 text-cool"}`}><Icon className="h-6 w-6" /></span>
                  <h3 className={`mt-4 text-xl font-semibold ${i === 0 ? "text-white" : "text-navy"}`}>{w.title}</h3>
                  <p className={`mt-2 text-sm leading-relaxed ${i === 0 ? "text-slate-200" : "text-slate-600"}`}>{w.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service areas */}
      <section className="py-16 md:py-24" data-testid="home-areas">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-sm font-bold uppercase tracking-[0.15em] text-launch">Proudly Local</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-navy tracking-tight">Serving Cocoa &amp; the Entire Space Coast</h2>
          </div>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {locations.map((l) => (
              <Link key={l.slug} to={`/service-areas/${l.slug}`} data-testid={`home-area-${l.slug}`} className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-4 py-3 text-sm font-medium text-navy hover:border-cool hover:text-cool transition-colors">
                <MapPin className="h-4 w-4 text-launch flex-shrink-0" /> {l.city}, FL
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Reviews reviews={reviews} />

      {/* Lead form split */}
      <section className="py-16 md:py-24 bg-slate-100" data-testid="home-lead">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.15em] text-launch">Get Started</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-navy tracking-tight">Request Your Free Estimate</h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">Tell us what you need and our Space Coast team will reach out fast. For urgent service, call us any time — day or night.</p>
            <div className="mt-6 space-y-3">
              <a href={`tel:${company.phone_href || ""}`} onClick={() => trackEvent("phone_click", { location: "home_lead" })} className="flex items-center gap-3 text-navy font-bold text-lg hover:text-launch transition-colors"><Phone className="h-5 w-5 text-launch" /> {company.phone}</a>
              <p className="flex items-center gap-3 text-slate-600"><Clock className="h-5 w-5 text-launch" /> {company.hours}</p>
              <p className="flex items-center gap-3 text-slate-600"><MapPin className="h-5 w-5 text-launch" /> {company.address}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8">
            <LeadForm />
          </div>
        </div>
      </section>

      <FaqAccordion faqs={homeFaqs} />
      <CtaBlock />
    </>
  );
}
